import React from 'react';

import { Alert, Flex, Skeleton, theme, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { RAW_INTERPOLATION } from '@app/services/i18n';

import { formatDecimal, getRowModules, sumPlaces } from '../../data/cargo';
import { COUNTERPARTY_ORDERS_PATH, CounterpartyOrderRow, WAREHOUSE_TAB_STATUSES } from '../../interfaces';
import { statusColor } from '../OrderStatusTag';

interface IProps {
  rows: CounterpartyOrderRow[];
  loading: boolean;
  total?: number;
  empty: React.ReactNode;
}

export const OrdersBoard: React.FC<IProps> = ({ rows, loading, total, empty }) => {
  const { token } = theme.useToken();
  const ordersT = useTranslation(NSKeys.counterpartyOrders);
  const locale = ordersT.i18n.language;

  if (loading && !rows.length) {
    return <Skeleton active />;
  }
  if (!rows.length) {
    return <>{empty}</>;
  }

  const cargoLine = (row: CounterpartyOrderRow): string => {
    if (row.cargo_unavailable) {
      return ordersT.t('cargoUnavailable');
    }
    const places = row.cargo?.places;
    const parts = [
      places && places.length ? ordersT.t('board.places', { count: sumPlaces(places) }) : undefined,
      row.cargo?.weight ? ordersT.t('board.weight', { value: formatDecimal(row.cargo.weight, locale) }) : undefined,
      row.cargo?.volume ? ordersT.t('board.volume', { value: formatDecimal(row.cargo.volume, locale) }) : undefined,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : '—';
  };

  return (
    <Flex vertical gap={12}>
      {total !== undefined && total > rows.length && (
        <Alert type="info" showIcon title={ordersT.t('board.truncated', { shown: rows.length, total })} />
      )}
      <Flex gap={12} style={{ overflowX: 'auto', paddingBottom: 4 }}>
        {WAREHOUSE_TAB_STATUSES.map((status) => {
          const items = rows.filter((row) => getRowModules(row).some((item) => item.status === status));
          return (
            <Flex
              key={status}
              vertical
              style={{
                width: 280,
                flex: 'none',
                borderTop: `4px solid ${statusColor(status, token)}`,
                borderRadius: token.borderRadius,
                background: token.colorFillQuaternary,
              }}
            >
              <Typography.Text strong style={{ padding: '10px 12px', textTransform: 'uppercase', fontSize: 13 }}>
                {`${ordersT.t(`status.${status}` as const)} (${items.length})`}
              </Typography.Text>
              <Flex vertical gap={8} style={{ padding: '0 8px 8px', minHeight: 120 }}>
                {items.length ? (
                  items.map((row) => (
                    <Flex
                      key={row.uid}
                      vertical
                      gap={4}
                      style={{
                        padding: 12,
                        background: token.colorBgContainer,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        borderRadius: token.borderRadiusLG,
                      }}
                    >
                      <Link to={`${COUNTERPARTY_ORDERS_PATH}/${row.uid}`} style={{ fontWeight: 600 }}>
                        {ordersT.t('number', { num: row.num })}
                      </Link>
                      <Typography.Text type="secondary">
                        {ordersT.t('board.customer', { title: row.customer.title, ...RAW_INTERPOLATION })}
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        {[row.delivery_city, row.delivery_address].filter(Boolean).join(' · ') || '—'}
                      </Typography.Text>
                      <Typography.Text type="secondary">{cargoLine(row)}</Typography.Text>
                    </Flex>
                  ))
                ) : (
                  <Typography.Text type="secondary" style={{ padding: '4px 4px 0' }}>
                    {ordersT.t('board.empty')}
                  </Typography.Text>
                )}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};
